import { Temporal } from 'temporal-polyfill'

/**
 * Get the current instant
 */
export const now = () => Temporal.Now.instant()

/**
 * Get the current ZonedDateTime in a specific timezone
 */
export const nowInTimeZone = (timeZone: string) =>
  Temporal.Now.zonedDateTimeISO(timeZone)

/**
 * Format a date string that may be in various formats (YYYYMMDD, YYYYMMDDTHHMMSS, ISO)
 * into a proper ISO date string
 */
export const formatDateString = (dateString: string): string => {
  if (dateString.length === 8) {
    return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`
  }
  return dateString
}

/**
 * Format a datetime string that may be in compact format into ISO format
 */
export const formatDateTimeString = (dateTimeString: string): string => {
  if (dateTimeString.includes('-')) {
    return dateTimeString
  }
  return `${dateTimeString.slice(0, 4)}-${dateTimeString.slice(4, 6)}-${dateTimeString.slice(6, 8)}T${dateTimeString.slice(9, 11)}:${dateTimeString.slice(11, 13)}:${dateTimeString.slice(13, 15)}`
}

/**
 * Parse a date/datetime string into a ZonedDateTime
 */
export const parseToZonedDateTime = (
  dateString: string,
  eventTimeZone: string,
  scheduleTimeZone: string,
): Temporal.ZonedDateTime => {
  // Handle UTC timestamps (ending with Z)
  if (dateString.includes('Z')) {
    const instant = Temporal.Instant.from(dateString)
    return instant.toZonedDateTimeISO(scheduleTimeZone)
  }

  // Handle date-only strings (no time component)
  if (dateString.length <= 10 && !dateString.includes('T')) {
    const { year, month, day } = Temporal.PlainDate.from(
      formatDateString(dateString),
    )

    const eventDateTime = Temporal.ZonedDateTime.from({
      timeZone: eventTimeZone,
      year,
      month,
      day,
      hour: 0,
      minute: 0,
      second: 0,
    })

    if (eventTimeZone === scheduleTimeZone) {
      return eventDateTime
    }
    return eventDateTime.withTimeZone(scheduleTimeZone)
  }

  // Handle datetime strings
  const { year, month, day, hour, minute, second } =
    Temporal.PlainDateTime.from(formatDateTimeString(dateString))

  const eventDateTime = Temporal.ZonedDateTime.from({
    year,
    month,
    day,
    hour,
    minute,
    second,
    timeZone: eventTimeZone,
  })

  if (eventTimeZone === scheduleTimeZone) {
    return eventDateTime
  }
  return eventDateTime.withTimeZone(scheduleTimeZone)
}

/**
 * Get a ZonedDateTime at midnight for the current day
 */
export const getMidnightToday = (timeZone: string): Temporal.ZonedDateTime => {
  return Temporal.Now.zonedDateTimeISO(timeZone).with({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
    microsecond: 0,
    nanosecond: 0,
  })
}

/**
 * Convert a ZonedDateTime to a FullCalendar-compatible string
 */
export const toFullCalendarDateString = (
  zdt: Temporal.ZonedDateTime,
): string => {
  return zdt.toPlainDateTime().toString().slice(0, 19).replace('T', ' ')
}

/**
 * Format time for display (e.g., "9:00 AM")
 */
export const formatTime = (
  zdt: Temporal.ZonedDateTime,
  locale = 'en-US',
): string => {
  return zdt.toPlainTime().toLocaleString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format date for display (e.g., "12/25/2024")
 */
export const formatDate = (
  zdt: Temporal.ZonedDateTime,
  locale = 'en-US',
): string => {
  return zdt.toPlainDate().toLocaleString(locale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

/**
 * Format date for display with abbreviated month (e.g., "Feb 24, 2026")
 */
export const formatDateLong = (
  zdt: Temporal.ZonedDateTime,
  locale = 'en-US',
): string => {
  return zdt.toPlainDate().toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format day of week (e.g., "Monday")
 */
export const formatDayOfWeek = (
  zdt: Temporal.ZonedDateTime,
  locale = 'en-US',
): string => {
  return zdt.toPlainDate().toLocaleString(locale, {
    weekday: 'long',
  })
}

/**
 * Compare two ZonedDateTimes
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
export const compareZonedDateTime = (
  a: Temporal.ZonedDateTime,
  b: Temporal.ZonedDateTime,
): number => {
  return Temporal.ZonedDateTime.compare(a, b)
}

/**
 * Check if a ZonedDateTime is between two other ZonedDateTimes
 */
export const isBetween = (
  target: Temporal.ZonedDateTime,
  start: Temporal.ZonedDateTime,
  end: Temporal.ZonedDateTime,
): boolean => {
  return (
    Temporal.ZonedDateTime.compare(start, target) <= 0 &&
    Temporal.ZonedDateTime.compare(target, end) <= 0
  )
}

/**
 * Parse an ISO duration string into a Temporal.Duration
 */
export const parseDuration = (durationString: string): Temporal.Duration => {
  return Temporal.Duration.from(durationString)
}

/**
 * Get yesterday's date as a JavaScript Date (for RRule compatibility)
 */
export const getYesterday = (): Date => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}

export { Temporal }
